import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Meta from "../../components/common/Meta";
import MainDesign from "../MainDesign";
import {processReferralCode} from "../../util/ReferralUtil";
import {ReferralService} from "../../service/ReferralService";
import AnimatedButton from "../../components/ui/motion/AnimatedButton";
import LoadingComponent from "../../components/common/LoadingComponent";
import {PurchaseService} from "../../service/PurchaseService";
import {ReferralValidation} from "../../model/user/referrals/Referrals";
import {motion} from "framer-motion";
import ReferralKedSalesPage from "../../components/referral/ReferralKedSalesPage";
import ReferralKedTeenSalesPage from "../../components/referral/ReferralKedTeenSalesPage";

type ErrorType = 'no_code' | 'invalid_code' | 'own_code' | 'network_error' | null;

interface ReferralKedPageProps {
    persona?: 'parent' | 'teen';
}

export default function ReferralKedPage({persona = 'parent'}: ReferralKedPageProps) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [validation, setValidation] = useState<ReferralValidation | null>(null);
    const [referralCode, setReferralCode] = useState<string | null>(null);
    const [transactionUrl, setTransactionUrl] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const [manualCode, setManualCode] = useState('');
    const [isValidating, setIsValidating] = useState(false);

    function validateCode(code: string) {
        setIsValidating(true);
        setError(null);

        ReferralService.validateReferralCode(code).then(response => {
            setValidation(response);
            setIsValidating(false);
            setLoading(false);
            if (response.valid) {
                setReferralCode(code);
                setError(null);
            } else {
                if (response.message?.includes('own')) {
                    setError('own_code');
                } else {
                    setError('invalid_code');
                }
                setReferralCode(code);
            }
        }).catch(() => {
            setIsValidating(false);
            setLoading(false);
            setError('network_error');
        });
    }

    useEffect(() => {
        const code = processReferralCode();
        if (!code) {
            setError('no_code');
            setLoading(false);
            return;
        }

        setReferralCode(code);
        validateCode(code);
    }, [navigate]);

    function handleManualCodeSubmit(e: React.FormEvent) {
        e.preventDefault();
        const trimmedCode = manualCode.trim().toUpperCase();
        if (trimmedCode.length > 0) {
            validateCode(trimmedCode);
        }
    }

    const initPurchase = async () => {
        if (!referralCode) {
            console.error('Kod polecający jest nieprawidłowy.');
            return;
        }
        try {
            setIsSubmitting(true);
            const redirectUrl = await PurchaseService.initKedV2ReferralPurchase(referralCode);
            window.open(redirectUrl, "_blank");
            setTransactionUrl(redirectUrl);
        } catch (error) {
            console.error('Error initiating purchase:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <MainDesign containerStyles="container py-md-28 py-16" useGradientBackground minHeight='100vh'>
                <LoadingComponent/>
            </MainDesign>
        );
    }

    if (error || !validation || !validation.valid) {
        const glassCard = {
            background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.35) 0%, rgba(26, 26, 46, 0.55) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
        };
        const glassCardLight = {
            background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.25) 0%, rgba(26, 26, 46, 0.4) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.04)',
        };

        const errorMessages: Record<string, { title: string; description: string; icon: string; accentColor: string }> = {
            no_code: {
                title: 'Brak kodu polecającego',
                description: 'Nie znaleziono kodu polecającego w linku. Upewnij się, że używasz poprawnego linku od znajomego.',
                icon: 'bi-link-45deg',
                accentColor: '#f59e0c',
            },
            invalid_code: {
                title: 'Nieprawidłowy kod polecający',
                description: 'Kod polecający jest nieprawidłowy lub nieaktywny. Sprawdź, czy link jest poprawny.',
                icon: 'bi-exclamation-triangle',
                accentColor: '#ef4444',
            },
            own_code: {
                title: 'To Twój własny kod',
                description: 'Nie możesz użyć własnego kodu polecającego. Podziel się nim ze znajomymi!',
                icon: 'bi-person-x',
                accentColor: '#f59e0c',
            },
            network_error: {
                title: 'Błąd połączenia',
                description: 'Nie udało się zweryfikować kodu polecającego. Spróbuj ponownie później.',
                icon: 'bi-wifi-off',
                accentColor: '#ef4444',
            }
        };

        const errorInfo = errorMessages[error || 'invalid_code'];

        return (
            <>
                <Meta
                    title="Kurs Efektywnego Działania - Błąd"
                    description="Nie udało się zweryfikować kodu polecającego"
                />
                <MainDesign minHeight='100vh' containerStyles="container py-md-16 py-10" useGradientBackground addOrb>
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-8 col-xl-6">
                            <motion.div
                                initial={{opacity: 0, y: 20}}
                                animate={{opacity: 1, y: 0}}
                                transition={{duration: 0.5}}
                                className="text-center"
                            >
                                <div className="rounded-4 p-4 p-md-5 mb-4 position-relative overflow-hidden" style={glassCard}>
                                    <div style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                                        background: `linear-gradient(90deg, ${errorInfo.accentColor} 0%, transparent 100%)`
                                    }}/>
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                        style={{
                                            width: '56px', height: '56px',
                                            background: `${errorInfo.accentColor}14`,
                                            border: `1px solid ${errorInfo.accentColor}30`,
                                        }}
                                    >
                                        <i className={`bi ${errorInfo.icon}`} style={{fontSize: '1.4rem', color: errorInfo.accentColor}}></i>
                                    </div>
                                    <h1 className="fs-5 text-white fw-bold mb-3">
                                        {errorInfo.title}
                                    </h1>
                                    <p className="text-secondary-light mb-0" style={{lineHeight: 1.7}}>
                                        {errorInfo.description}
                                    </p>
                                    {referralCode && (
                                        <p className="text-secondary-light mt-3 mb-0">
                                            Kod: <span className="fw-bold" style={{
                                                color: errorInfo.accentColor,
                                                letterSpacing: '0.15em',
                                                fontFamily: '"Courier New", Courier, monospace',
                                            }}>{referralCode}</span>
                                        </p>
                                    )}
                                </div>

                                {/* Manual code input */}
                                <div className="rounded-4 p-4 p-md-5 mb-4" style={glassCard}>
                                    <h6 className="text-white mb-1 fw-semibold" style={{fontSize: '0.95rem'}}>
                                        Masz kod polecający?
                                    </h6>
                                    <p className="text-secondary-light mb-4" style={{fontSize: '0.85rem'}}>
                                        Wpisz go poniżej, aby aktywować zniżkę
                                    </p>
                                    <form onSubmit={handleManualCodeSubmit}>
                                        <div className="d-flex gap-2 justify-content-center">
                                            <input
                                                type="text"
                                                className="form-control text-white text-center text-uppercase"
                                                placeholder="Wpisz kod"
                                                value={manualCode}
                                                onChange={(e) => setManualCode(e.target.value)}
                                                style={{
                                                    maxWidth: '220px',
                                                    letterSpacing: '3px',
                                                    background: 'rgba(255, 255, 255, 0.04)',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '12px',
                                                    padding: '0.6rem 1rem',
                                                }}
                                                disabled={isValidating}
                                            />
                                            <AnimatedButton
                                                type="submit"
                                                className="btn btn-success fw-bold px-4"
                                                style={{borderRadius: '12px'}}
                                                disabled={isValidating || manualCode.trim().length === 0}
                                            >
                                                {isValidating ? (
                                                    <span className="spinner-border spinner-border-sm" role="status"/>
                                                ) : (
                                                    'Sprawdź'
                                                )}
                                            </AnimatedButton>
                                        </div>
                                    </form>
                                </div>

                                <div className="rounded-4 p-4 p-md-5" style={glassCardLight}>
                                    <p className="text-secondary-light mb-3" style={{fontSize: '0.9rem'}}>
                                        Nie masz kodu? Nadal możesz dołączyć do Kursu Efektywnego Działania!
                                    </p>
                                    <AnimatedButton
                                        className="btn btn-outline-success fw-bold px-4 py-2"
                                        onClick={() => navigate('/oferta/kurs-efektywnego-dzialania')}
                                        style={{borderRadius: '12px'}}
                                    >
                                        <i className="bi bi-arrow-right-circle me-2"></i>
                                        Przejdź do standardowej oferty
                                    </AnimatedButton>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </MainDesign>
            </>
        );
    }

    const SalesComponent = persona === 'teen' ? ReferralKedTeenSalesPage : ReferralKedSalesPage;
    const metaTitle = persona === 'teen'
        ? "Kurs Efektywnego Działania - Oferta dla Ciebie"
        : "Kurs Efektywnego Działania - Oferta od Znajomego";
    const metaDescription = persona === 'teen'
        ? `${validation.referrerName || 'Twój znajomy'} zaprasza Cię do Kursu Efektywnego Działania. Zacznij działać efektywniej — w najlepszej cenie.`
        : `${validation.referrerName || 'Twój znajomy'} zaprasza Cię do Kursu Efektywnego Działania. To najlepsza cena, jaką można otrzymać poza naszymi szkoleniami.`;

    return <>
        <Meta
            title={metaTitle}
            description={metaDescription}
        />
        <MainDesign containerStyles="container py-md-16 py-10" useGradientBackground addOrb>
            <SalesComponent
                referralCode={referralCode!}
                referrerName={validation.referrerName ?? undefined}
                onInitPurchase={initPurchase}
                isSubmitting={isSubmitting}
                transactionUrl={transactionUrl}
            />
        </MainDesign>
    </>;
}